import React, { useMemo } from 'react';

import { COLORS } from 'lib/theme';
import { CONGESTION_LEVELS } from 'lib/constants';
import Container from 'components/Container';
import GaugeChart from 'components/GaugeChart';
import InformationBoard from 'components/InformationBoard';
import SensorsMap from 'components/SensorsMap';
import { fetchRealtimeVisitorCount } from 'lib/api';
import { getCongestionLevel } from 'lib/utils';
import { sensors } from 'lib/config';
import styled from 'styled-components';
import { useQueries } from 'react-query';

const ChartsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 30px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;

  & div.chart {
    background-color: ${COLORS.transparentWhite50};
    box-shadow: 0px 4px 9px 0px rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    height: 210px;
    margin: 15px 2px 30px 20px;
  }

  & div.name {
    color: ${COLORS.light};
    font-size: 17px;
    font-weight: bold;
    padding-top: 30px;
    position: relative;
    text-align: center;
    & small {
      color: ${COLORS.gray};
      position: absolute;
      top: 7px;
      right: 10px;
      font-size: 10px;
      font-weight: normal;
    }
  }
`;

export default function KioskMap() {
  const SENSORS = useMemo(
    () => Object.values(sensors).sort((a, b) => a.index - b.index),
    []
  );

  const results = useQueries(
    SENSORS.map((sensor) => ({
      queryKey: ['sensor', sensor.id],
      queryFn: fetchRealtimeVisitorCount,
      refetchInterval: 60000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    }))
  );

  const resultData = useMemo(
    () =>
      results.map((result, i) => {
        const sensor = SENSORS[i];
        const data = result?.data?.data || {};
        const count = data?.count || 0;
        const max = sensor.area / CONGESTION_LEVELS.congested.distance;
        const level = getCongestionLevel(count, sensor.area);

        return {
          id: sensor.id,
          name: sensor.name,
          count,
          level,
          max,
        };
      }),
    [results]
  );

  return (
    <Container>
      <InformationBoard />
      <SensorsMap sensors={SENSORS} data={resultData} />
      <ChartsContainer>
        {resultData.map((data, i) => {
          const sensor = SENSORS[i];
          const { count, level, max } = data;
          return (
            <div key={sensor.id} className="chart">
              <div className="name">
                {sensor.name}
                <small>
                  {sensor.area} m<sup>2</sup>
                </small>
              </div>
              <div style={{ height: 170, marginTop: '-0.5rem' }}>
                <GaugeChart
                  data={
                    count
                      ? [
                          {
                            title: sensor.name,
                            value: count > max ? max : count,
                            color: level.fill,
                          },
                        ]
                      : [
                          {
                            title: sensor.name,
                            value: 0,
                          },
                        ]
                  }
                  options={{
                    fill: level.fill,
                    level: level.name,
                    labelPosition: 0,
                    labelStyle: {
                      fontSize: '10px',
                      fontFamily: 'sans-serif',
                      fill: level.fill,
                    },
                    totalValue: max,
                  }}
                />
              </div>
            </div>
          );
        })}
      </ChartsContainer>
    </Container>
  );
}
